import type { ResolvedPos } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';

export interface DropTargetTracker {
  /**
   * `handleDOMEvents.drop` body. The literal `false` return type pins the
   * observe-only contract — ProseMirror's own drop handling must still run.
   */
  captureDrop: (view: EditorView, event: DragEvent) => false;
  /** Where a `transformPasted` slice is about to land: drop point, else caret. */
  resolveContext: (view: EditorView) => ResolvedPos;
}

/**
 * Tracks where an in-flight drop will land.
 *
 * ProseMirror runs `transformPasted` for drags as well as pastes but hands it
 * only the view, so a transform that judges its target — `normalizePastedSections`
 * deciding whether a pasted section fits — would otherwise read the caret and
 * misjudge drops into a different container.
 *
 * `captureDrop` runs before ProseMirror's own drop handler in the same
 * synchronous listener, so the transform sees the position. The microtask
 * clears it once the drop settles, which also covers the paths where
 * ProseMirror bails before transforming anything.
 *
 * One tracker per plugin instance. A capture resolves only against the view
 * that recorded it; any other view falls back to its caret.
 */
export const createDropTargetTracker = (): DropTargetTracker => {
  let captured: { view: EditorView; pos: number } | null = null;

  return {
    captureDrop: (view, event) => {
      const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos;
      captured = pos === undefined ? null : { view, pos };
      queueMicrotask(() => {
        captured = null;
      });
      return false;
    },

    resolveContext: (view) =>
      captured !== null && captured.view === view
        ? view.state.doc.resolve(captured.pos)
        : view.state.selection.$from,
  };
};
